/**
 * 统一分类筛选模块
 * 合并产品分类和新闻分类功能，通过配置对象区分行为
 *
 * @param {Object} config
 * @param {string} config.type - 类型：'product' | 'news'
 * @param {string} config.containerSelector - 内容容器选择器
 * @param {string} config.categorySelector - 分类导航选择器
 * @param {number} config.pageSize - 每页数量
 * @param {boolean} config.showPagination - 是否显示分页
 * @param {string} config.basePath - 基础路径（如 '/products' 或 '/news'）
 */
export function initCategoryFilter(config) {
  const {
    type,
    containerSelector,
    categorySelector,
    pageSize = 10,
    showPagination = true,
    basePath,
  } = config;

  // 查找容器和分类导航
  const container = document.querySelector(containerSelector);
  const categoryList = document.querySelector(categorySelector);
  if (!container || !categoryList) return;

  let currentPage = 1;
  let currentCategoryId = null;
  // 标记是否已由用户交互触发过请求（首次加载使用 SSR 内容）
  let userInteracted = false;

  // ========== 渲染函数 ==========

  /**
   * 创建产品卡片元素（DOM API，防 XSS）
   */
  function createProductCard(product) {
    const link = document.createElement('a');
    link.href = `/products/${product.id}`;
    link.className = 'product-card-link';

    const card = document.createElement('div');
    card.className = 'product-card';

    const imageDiv = document.createElement('div');
    imageDiv.className = 'product-image';
    const img = document.createElement('img');
    img.src = product.image;
    img.alt = product.name;
    img.loading = 'lazy';
    imageDiv.appendChild(img);

    const infoDiv = document.createElement('div');
    infoDiv.className = 'product-info';
    const h3 = document.createElement('h3');
    h3.textContent = product.name;
    infoDiv.appendChild(h3);

    card.appendChild(imageDiv);
    card.appendChild(infoDiv);
    link.appendChild(card);
    return link;
  }

  /**
   * 创建新闻列表项元素（DOM API，防 XSS）
   */
  function createNewsItem(news) {
    const item = document.createElement('div');
    item.className = 'news-item';
    item.addEventListener('click', () => {
      window.location.href = `/news/${news.ID}`;
    });

    const content = document.createElement('div');
    content.className = 'news-item-content';

    const title = document.createElement('h3');
    title.className = 'news-item-title';
    title.textContent = news.Title;

    const categorySpan = document.createElement('span');
    categorySpan.className = 'news-item-category';
    categorySpan.textContent = news.Category;
    title.appendChild(categorySpan);

    const meta = document.createElement('div');
    meta.className = 'news-item-meta';
    const dateSpan = document.createElement('span');
    dateSpan.className = 'news-item-date';
    dateSpan.textContent = `发布时间: ${news.Date}`;
    meta.appendChild(dateSpan);

    content.appendChild(title);
    content.appendChild(meta);
    item.appendChild(content);
    return item;
  }

  /**
   * 渲染产品列表
   */
  function renderProducts(data) {
    container.textContent = '';

    if (data.products && data.products.products && data.products.products.length > 0) {
      data.products.products.forEach(product => {
        container.appendChild(createProductCard(product));
      });

      // 渲染分页
      if (data.products.pagination) {
        renderPagination(data.products.pagination.total_pages, currentPage);
      }
    } else {
      const empty = document.createElement('div');
      empty.className = 'no-products';
      const p = document.createElement('p');
      p.textContent = '该分类暂无产品';
      empty.appendChild(p);
      container.appendChild(empty);
    }
  }

  /**
   * 渲染新闻列表
   */
  function renderNews(data) {
    container.textContent = '';

    if (data.news && data.news.length > 0) {
      data.news.forEach(news => {
        container.appendChild(createNewsItem(news));
      });

      // 渲染分页
      if (data.totalPages > 1) {
        renderPagination(data.totalPages, data.currentPageNum, data.pageNumbers);
      }
    } else {
      const empty = document.createElement('div');
      empty.className = 'news-empty';
      const p = document.createElement('p');
      p.textContent = '暂无新闻';
      empty.appendChild(p);
      container.appendChild(empty);
    }
  }

  // ========== 分页 ==========

  /**
   * 渲染分页控件（使用 DOM API）
   */
  function renderPagination(totalPages, page, pageNumbers) {
    if (!showPagination || totalPages <= 1) {
      // 清空已有分页
      const existing = document.querySelector('.pagination');
      if (existing) existing.textContent = '';
      return;
    }

    // 获取或创建分页容器
    let paginationContainer = document.querySelector('.pagination');
    if (!paginationContainer) {
      // 新闻列表把分页放在容器内
      if (type === 'news') {
        paginationContainer = document.createElement('div');
        paginationContainer.className = 'pagination';
        container.appendChild(paginationContainer);
      } else {
        return;
      }
    }
    paginationContainer.textContent = '';

    const categoryParam = currentCategoryId ? `&category=${currentCategoryId}` : '';

    // 上一页
    if (page > 1) {
      const prev = document.createElement('a');
      prev.href = `${basePath}?page=${page - 1}${categoryParam}`;
      prev.className = 'page-link prev';
      prev.innerHTML = '&laquo; 上一页';
      paginationContainer.appendChild(prev);
    }

    // 页码按钮
    const pages = pageNumbers || Array.from({ length: totalPages }, (_, i) => i + 1);
    pages.forEach(num => {
      const link = document.createElement('a');
      link.href = `${basePath}?page=${num}${categoryParam}`;
      link.className = 'page-link' + (num === page ? ' active' : '');
      link.textContent = num;
      paginationContainer.appendChild(link);
    });

    // 下一页
    if (page < totalPages) {
      const next = document.createElement('a');
      next.href = `${basePath}?page=${page + 1}${categoryParam}`;
      next.className = 'page-link next';
      next.innerHTML = '下一页 &raquo;';
      paginationContainer.appendChild(next);
    }

    // 分页点击事件委托
    paginationContainer.addEventListener('click', handlePaginationClick);
  }

  /**
   * 分页点击处理
   */
  function handlePaginationClick(e) {
    const link = e.target.closest('.page-link');
    if (!link) return;
    e.preventDefault();

    const href = link.getAttribute('href');
    const pageMatch = href.match(/page=(\d+)/);
    const categoryMatch = href.match(/category=([^&]+)/);

    if (pageMatch && pageMatch[1]) {
      const pageNum = parseInt(pageMatch[1], 10);
      const catId = categoryMatch && categoryMatch[1] ? categoryMatch[1] : currentCategoryId;

      loadData(catId, pageNum);

      const newUrl = catId
        ? `${basePath}?page=${pageNum}&category=${catId}`
        : `${basePath}?page=${pageNum}`;
      history.pushState({ categoryId: catId, page: pageNum }, '', newUrl);
    }
  }

  // ========== 数据加载 ==========

  /**
   * 加载数据（AJAX）
   */
  async function loadData(categoryId, page = 1) {
    currentCategoryId = categoryId;
    currentPage = page;
    userInteracted = true;

    // 显示加载状态
    container.textContent = '';
    const loading = document.createElement('div');
    loading.className = 'loading';
    const p = document.createElement('p');
    p.textContent = '加载中...';
    loading.appendChild(p);
    container.appendChild(loading);

    try {
      // 构建请求 URL
      const params = new URLSearchParams();
      if (page) params.append('page', page);
      if (categoryId) params.append('category', categoryId);
      if (type === 'product') params.append('pageSize', pageSize);
      const url = params.toString() ? `${basePath}?${params}` : basePath;

      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();

        if (type === 'product') {
          renderProducts(data);
        } else {
          renderNews(data);
        }
      } else if (type === 'news' && contentType && contentType.includes('text/html')) {
        // 新闻支持 HTML 响应降级
        const html = await response.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const newContent = tempDiv.querySelector(containerSelector);
        if (newContent) {
          container.innerHTML = newContent.innerHTML;
        } else {
          showEmpty();
        }
      } else {
        showEmpty();
      }
    } catch (error) {
      console.error(`加载${type === 'product' ? '产品' : '新闻'}数据失败:`, error);
      showEmpty();
    }
  }

  /**
   * 显示空状态
   */
  function showEmpty() {
    container.textContent = '';
    const empty = document.createElement('div');
    empty.className = type === 'product' ? 'no-products' : 'news-empty';
    const p = document.createElement('p');
    p.textContent = type === 'product' ? '该分类暂无产品' : '加载失败，请刷新页面重试';
    empty.appendChild(p);
    container.appendChild(empty);

    // 清空分页
    const pagination = document.querySelector('.pagination');
    if (pagination) pagination.textContent = '';
  }

  // ========== 分类点击 ==========

  /** 更新分类菜单高亮状态：在 <a> 上切换 BEM 修饰类 */
  function updateActiveCategory(activeLink) {
    categoryList.querySelectorAll('a').forEach(link => link.classList.remove('category-nav__link--active'));
    if (activeLink) {
      activeLink.classList.add('category-nav__link--active');
    }
  }

  categoryList.addEventListener('click', (e) => {
    const categoryItem = e.target.closest('a');
    if (!categoryItem) return;
    e.preventDefault();

    // 更新激活状态
    updateActiveCategory(categoryItem);

    // 获取分类 ID
    let categoryId;
    if (type === 'product') {
      categoryId = categoryItem.getAttribute('category');
    } else {
      const href = categoryItem.getAttribute('href');
      const params = new URLSearchParams(href.split('?')[1]);
      categoryId = params.get('category');
    }

    // 重置页码并加载数据
    currentPage = 1;
    const pagination = document.querySelector('.pagination');
    if (pagination) pagination.textContent = '';

    loadData(categoryId, 1);

    // 更新 URL
    const newUrl = categoryId
      ? `${basePath}?page=1&category=${categoryId}`
      : basePath;
    history.pushState({ categoryId, page: 1 }, '', newUrl);
  });

  // ========== History API（前进/后退） ==========

  window.addEventListener('popstate', (e) => {
    let catId, page;
    if (e.state) {
      catId = e.state.categoryId;
      page = e.state.page;
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      catId = urlParams.get('category') || null;
      page = parseInt(urlParams.get('page'), 10) || 1;
    }
    // 更新分类高亮（根据 categoryId 找到对应的 <a> 并高亮）
    const matchedLink = catId
      ? categoryList.querySelector(`a[href*="category=${catId}"], a[category="${catId}"]`)
      : categoryList.querySelector('li:first-child a');
    updateActiveCategory(matchedLink);
    loadData(catId, page);
  });

  // ========== 初始化 ==========

  // 保存初始 history 状态
  const urlParams = new URLSearchParams(window.location.search);
  const initialCategoryId = urlParams.get('category') || null;
  const initialPage = parseInt(urlParams.get('page'), 10) || 1;

  history.replaceState(
    { categoryId: initialCategoryId, page: initialPage },
    '',
    window.location.href
  );

  // 首次加载逻辑：
  // - 如果 SSR 已渲染了内容（container 不为空），直接使用 DOM 中的内容
  // - 只有当 URL 有分类参数时才发请求（说明用户带参数进入）
  if (type === 'product') {
    // 产品分类：首页和列表页行为不同
    // 如果容器已有内容且没有 URL 分类参数，使用 SSR 内容
    if (container.children.length > 0 && !initialCategoryId) {
      // SSR 已渲染，不发请求
      // 设置第一个分类为激活状态（SSR 已在 <a> 上加了 --active，但保险起见也用 JS 设置）
      const firstCategory = categoryList.querySelector('li:first-child a');
      if (firstCategory) {
        updateActiveCategory(firstCategory);
        currentCategoryId = firstCategory.getAttribute('category');
      }
    } else {
      // 需要加载数据
      const firstCategory = categoryList.querySelector('li:first-child a');
      if (firstCategory) {
        updateActiveCategory(firstCategory);
        const catId = initialCategoryId || firstCategory.getAttribute('category');
        loadData(catId, initialPage);
      }
    }
  } else {
    // 新闻分类：如果容器已有内容且没有 URL 分类参数，使用 SSR 内容
    if (container.children.length > 0 && !initialCategoryId) {
      // SSR 已渲染，不发请求
      const firstCategoryLink = categoryList.querySelector('li a');
      if (firstCategoryLink) {
        updateActiveCategory(firstCategoryLink);
        const href = firstCategoryLink.getAttribute('href');
        const params = new URLSearchParams(href.split('?')[1]);
        currentCategoryId = params.get('category');
      }
    } else if (!initialCategoryId) {
      // 没有分类参数，加载第一个分类
      const firstCategoryLink = categoryList.querySelector('li a');
      if (firstCategoryLink) {
        updateActiveCategory(firstCategoryLink);
        const href = firstCategoryLink.getAttribute('href');
        const params = new URLSearchParams(href.split('?')[1]);
        const firstCategoryId = params.get('category');
        loadData(firstCategoryId, 1);
        const newUrl = `${basePath}?category=${firstCategoryId}`;
        history.replaceState({ categoryId: firstCategoryId, page: 1 }, '', newUrl);
      }
    }
  }
}
